import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, VStack, Heading, Text, Button, HStack, Badge } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import {
  GENETIC_TEST_RESULT_LABELS,
  GENETIC_TEST_RESULT_COLORS,
} from '@/types/pdfAnalysis';
import { useDogStore } from '@/stores/dogStore';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layout/AppLayout';
import { PhotoGallery } from '@/components/dog/PhotoGallery';
import { PhotoUploader } from '@/components/dog/PhotoUploader';
import { DocumentUploader } from '@/components/dog/DocumentUploader';
import { DocumentViewer } from '@/components/dog/DocumentViewer';
import { BirthRecordList } from '@/components/birthRecord/BirthRecordList';
import { useBirthRecordStore } from '@/stores/birthRecordStore';
import { MatingRecordList } from '@/components/matingRecord/MatingRecordList';
import { useMatingRecordStore } from '@/stores/matingRecordStore';
import { PedigreeTree } from '@/components/pedigree/PedigreeTree';
import { AIMatingProposalModal } from '@/components/ai/AIMatingProposalModal';
import { DeleteDogDialog } from '@/components/dog/DeleteDogDialog';
import { DogIdentityLabel } from '@/components/dog/DogIdentityLabel';
import { VaccinationSection } from '@/components/vaccination/VaccinationSection';
import { pedigreeService } from '@/services/firebase/pedigreeService';
import { documentService } from '@/services/firebase/documentService';
import { PedigreeTree as PedigreeTreeType } from '@/types/pedigree';
import { DogDocument } from '@/types/dog';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { FiList, FiZap } from 'react-icons/fi';
import {
  calculateBreedingStatus,
  getBreedingStatusLabel,
  getBreedingStatusColor,
} from '@/utils/breedingStatusUtils';

/**
 * 個体詳細ページコンポーネント（仮実装）
 */
const DogDetailPage: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { selectedDog, loading, error, fetchDog, deleteDog } = useDogStore();
  const {
    birthRecords,
    loading: birthRecordsLoading,
    fetchBirthRecordsByMother,
    fetchBirthRecordsByFather,
  } = useBirthRecordStore();
  const {
    matingRecords,
    loading: matingRecordsLoading,
    fetchMatingRecordsByMale,
    fetchMatingRecordsByFemale,
  } = useMatingRecordStore();
  const [pedigree, setPedigree] = useState<PedigreeTreeType | null>(null);
  const [pedigreeLoading, setPedigreeLoading] = useState(false);
  const [pedigreeDocument, setPedigreeDocument] = useState<DogDocument | null>(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (user && id) {
      fetchDog(id, user.uid);
    }
  }, [user, id, fetchDog]);

  // 出産記録の取得（性別に応じて）
  useEffect(() => {
    if (user && id && selectedDog) {
      if (selectedDog.gender === 'female') {
        fetchBirthRecordsByMother(id, user.uid);
      } else if (selectedDog.gender === 'male') {
        fetchBirthRecordsByFather(id, user.uid);
      }
    }
  }, [user, id, selectedDog]);

  // 交配記録の取得（性別に応じて）
  useEffect(() => {
    if (user && id && selectedDog) {
      if (selectedDog.gender === 'male') {
        fetchMatingRecordsByMale(id, user.uid);
      } else if (selectedDog.gender === 'female') {
        fetchMatingRecordsByFemale(id, user.uid);
      }
    }
  }, [user, id, selectedDog]);

  // 血統図の取得
  useEffect(() => {
    const fetchPedigree = async () => {
      if (user && id && selectedDog) {
        // 親犬情報がある場合のみ取得
        if (selectedDog.fatherId || selectedDog.motherId) {
          setPedigreeLoading(true);
          try {
            const pedigreeData = await pedigreeService.getPedigreeTree(id, user.uid);
            setPedigree(pedigreeData);
          } catch (error) {
            console.error('血統図取得エラー:', error);
          } finally {
            setPedigreeLoading(false);
          }
        }
      }
    };

    fetchPedigree();
  }, [user, id, selectedDog]);

  // 血統書の取得
  useEffect(() => {
    const fetchDocument = async () => {
      if (user && id && selectedDog) {
        setDocumentLoading(true);
        try {
          const document = await documentService.getDocument(id, user.uid);
          setPedigreeDocument(document);
        } catch (error) {
          console.error('血統書取得エラー:', error);
        } finally {
          setDocumentLoading(false);
        }
      }
    };

    fetchDocument();
  }, [user, id, selectedDog]);

  // 個体削除ハンドラ
  const handleDelete = async (permanent: boolean) => {
    if (!user || !id) return;

    setIsDeleting(true);
    try {
      await deleteDog(id, user.uid, permanent);
      toaster.success({
        title: permanent ? '個体を完全に削除しました' : 'ステータスを「死亡」に変更しました',
      });
      navigate('/dogs');
    } catch (error) {
      console.error('削除エラー:', error);
      toaster.error({
        title: '削除に失敗しました',
        description: error instanceof Error ? error.message : '不明なエラーが発生しました',
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  // 血統書の再取得（アップロード・削除後）
  const handleDocumentChange = async () => {
    if (user && id) {
      setDocumentLoading(true);
      try {
        const document = await documentService.getDocument(id, user.uid);
        setPedigreeDocument(document);
        // 個体情報も再取得（pedigreeDocumentUrlが更新されるため）
        await fetchDog(id, user.uid);
      } catch (error) {
        console.error('血統書再取得エラー:', error);
      } finally {
        setDocumentLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Text textAlign="center">読み込み中...</Text>
      </AppLayout>
    );
  }

  if (error || !selectedDog) {
    return (
      <AppLayout>
        <Box maxW="4xl" mx="auto">
          <Box p={4} bg="red.50" borderRadius="md" color="red.700">
            <Text>{error || '個体が見つかりません'}</Text>
          </Box>
          <HStack gap={2} mt={4}>
            <Button onClick={() => navigate('/dogs')}>
              <FiList size={16} />
              <Text ml={2}>一覧に戻る</Text>
            </Button>
          </HStack>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box maxW="4xl" mx="auto">
        <VStack gap={6} align="stretch">
          {/* ヘッダー */}
          <HStack justify="space-between" flexWrap="wrap" gap={4} align="flex-start">
            <DogIdentityLabel dog={selectedDog} size="lg" showRegistrationNumbers />
            <HStack gap={2} flexWrap="wrap">
              <Button variant="outline" onClick={() => navigate('/dogs')} size="sm">
                <FiList size={16} />
                <Text ml={2}>一覧に戻る</Text>
              </Button>
              <Button colorPalette="blue" onClick={() => navigate(`/dogs/${id}/edit`)} size="sm">
                編集
              </Button>
              <Button
                colorPalette="red"
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(true)}
                size="sm"
              >
                削除
              </Button>
            </HStack>
          </HStack>

        {/* 基本情報 */}
        <Box bg="white" p={6} borderRadius="lg" shadow="md">
          <VStack align="stretch" gap={4}>
            <Heading size="md">基本情報</Heading>

            <HStack gap={4}>
              <Badge colorPalette={selectedDog.gender === 'male' ? 'blue' : 'pink'}>
                {selectedDog.gender === 'male' ? 'オス' : 'メス'}
              </Badge>
              <Badge
                colorPalette={
                  selectedDog.status === 'active'
                    ? 'green'
                    : selectedDog.status === 'transferred'
                      ? 'gray'
                      : 'red'
                }
              >
                {selectedDog.status === 'active'
                  ? '在籍中'
                  : selectedDog.status === 'transferred'
                    ? '譲渡済み'
                    : '死亡'}
              </Badge>
            </HStack>

            {selectedDog.breed && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  犬種
                </Text>
                <Text fontWeight="medium">{selectedDog.breed}</Text>
              </Box>
            )}

            {selectedDog.ownerName && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  所有者
                </Text>
                <Text fontWeight="medium">{selectedDog.ownerName}</Text>
              </Box>
            )}

            {selectedDog.jkcRegistrationNumber && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  JKC登録番号
                </Text>
                <Text fontWeight="medium">{selectedDog.jkcRegistrationNumber}</Text>
              </Box>
            )}

            {selectedDog.dnaRegistrationNumber && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  DNA登録番号
                </Text>
                <Text fontWeight="medium">{selectedDog.dnaRegistrationNumber}</Text>
              </Box>
            )}

            {selectedDog.microchipNumber && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  マイクロチップ番号
                </Text>
                <Text fontWeight="medium" fontFamily="mono">
                  {selectedDog.microchipNumber}
                </Text>
              </Box>
            )}

            {selectedDog.microchipPin && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  マイクロチップ暗証記号
                </Text>
                <Text fontWeight="medium" fontFamily="mono">
                  {selectedDog.microchipPin}
                </Text>
              </Box>
            )}

            {selectedDog.breederName && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  繁殖者名
                </Text>
                <Text fontWeight="medium">{selectedDog.breederName}</Text>
              </Box>
            )}

            <Box>
              <Text fontSize="sm" color="gray.600">
                出生日
              </Text>
              <Text fontWeight="medium">
                {format(selectedDog.birthDate.toDate(), 'yyyy年M月d日', { locale: ja })}
              </Text>
            </Box>

            {/* 出産可能ステータス（メス犬のみ） */}
            {selectedDog.gender === 'female' && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  繁殖ステータス
                </Text>
                {(() => {
                  const breedingStatus = calculateBreedingStatus(selectedDog, birthRecords);
                  return (
                    <VStack align="start" gap={3}>
                      <Badge colorPalette={getBreedingStatusColor(breedingStatus.status)}>
                        {getBreedingStatusLabel(breedingStatus.status)}
                      </Badge>
                      {breedingStatus.status === 'resting' &&
                        breedingStatus.nextAvailableDate && (
                          <Text fontSize="sm" color="gray.600">
                            次回交配可能日:{' '}
                            {format(breedingStatus.nextAvailableDate, 'yyyy年M月d日', {
                              locale: ja,
                            })}{' '}
                            (あと{breedingStatus.daysUntilAvailable}日)
                          </Text>
                        )}
                      {breedingStatus.status === 'available' && breedingStatus.lastBirthDate && (
                        <Text fontSize="sm" color="gray.600">
                          最終出産日:{' '}
                          {format(breedingStatus.lastBirthDate, 'yyyy年M月d日', { locale: ja })}
                        </Text>
                      )}

                      {/* AI交配提案ボタン */}
                      <Box
                        p={3}
                        bg="blue.50"
                        borderRadius="md"
                        borderLeft="4px solid"
                        borderLeftColor="blue.500"
                        width="100%"
                      >
                        <VStack align="start" gap={2}>
                          <Text fontSize="sm" fontWeight="medium" color="blue.700">
                            AI交配提案
                          </Text>
                          <Text fontSize="xs" color="blue.600">
                            AIが最適なオス犬の組み合わせを提案します
                          </Text>
                          <Button
                            size="sm"
                            colorPalette="blue"
                            onClick={() => setIsAIModalOpen(true)}
                          >
                            <HStack gap={1}>
                              <FiZap />
                              <Text>AI提案を見る</Text>
                            </HStack>
                          </Button>
                        </VStack>
                      </Box>
                    </VStack>
                  );
                })()}
              </Box>
            )}

            {selectedDog.characteristics && (
              <>
                {selectedDog.characteristics.temperament && (
                  <Box>
                    <Text fontSize="sm" color="gray.600">
                      性格
                    </Text>
                    <Text fontWeight="medium">{selectedDog.characteristics.temperament}</Text>
                  </Box>
                )}

                {selectedDog.characteristics.size && (
                  <Box>
                    <Text fontSize="sm" color="gray.600">
                      体格
                    </Text>
                    <Text fontWeight="medium">{selectedDog.characteristics.size}</Text>
                  </Box>
                )}

                {selectedDog.characteristics.color && (
                  <Box>
                    <Text fontSize="sm" color="gray.600">
                      毛色
                    </Text>
                    <Text fontWeight="medium">{selectedDog.characteristics.color}</Text>
                  </Box>
                )}
              </>
            )}

            {selectedDog.notes && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  備考
                </Text>
                <Text>{selectedDog.notes}</Text>
              </Box>
            )}
          </VStack>
        </Box>

        {/* 遺伝子検査結果セクション */}
        {selectedDog.geneticTestResults && selectedDog.geneticTestResults.length > 0 && (
          <Box bg="white" p={6} borderRadius="lg" shadow="md">
            <VStack align="stretch" gap={4}>
              <Heading size="md">遺伝子検査結果</Heading>

              <VStack gap={2} align="stretch">
                {selectedDog.geneticTestResults.map((result, index) => (
                  <HStack
                    key={index}
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    justify="space-between"
                  >
                    <VStack align="start" gap={1}>
                      <Text fontWeight="medium" fontSize="sm">
                        {result.testName}
                      </Text>
                      {result.laboratory && (
                        <Text fontSize="xs" color="gray.500">
                          検査機関: {result.laboratory}
                        </Text>
                      )}
                    </VStack>
                    <Badge colorPalette={GENETIC_TEST_RESULT_COLORS[result.result]}>
                      {GENETIC_TEST_RESULT_LABELS[result.result]}
                    </Badge>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </Box>
        )}

        {/* 交配履歴セクション */}
        {user && id && selectedDog && (
          <Box bg="white" p={6} borderRadius="lg" shadow="md">
            <VStack align="stretch" gap={4}>
              <HStack justify="space-between">
                <Heading size="md">
                  {selectedDog.gender === 'male' ? '交配履歴（オス犬として）' : '交配履歴（メス犬として）'}
                </Heading>
                <Button
                  size="sm"
                  colorPalette="blue"
                  onClick={() => navigate('/mating-records/new')}
                >
                  新規登録
                </Button>
              </HStack>
              <MatingRecordList
                matingRecords={matingRecords}
                userId={user.uid}
                loading={matingRecordsLoading}
                onEdit={(recordId) => navigate(`/mating-records/${recordId}/edit`)}
              />
            </VStack>
          </Box>
        )}

        {/* 出産履歴セクション（メス犬のみ・交配履歴の直下） */}
        {user && id && selectedDog && selectedDog.gender === 'female' && (
          <Box bg="white" p={6} borderRadius="lg" shadow="md">
            <VStack align="stretch" gap={4}>
              <HStack justify="space-between">
                <Heading size="md">出産履歴（母犬として）</Heading>
                <Button
                  size="sm"
                  colorPalette="blue"
                  onClick={() => navigate('/birth-records/new')}
                >
                  新規登録
                </Button>
              </HStack>
              <BirthRecordList
                birthRecords={birthRecords}
                userId={user.uid}
                loading={birthRecordsLoading}
                onEdit={(recordId) => navigate(`/birth-records/${recordId}/edit`)}
              />
            </VStack>
          </Box>
        )}

        {/* ワクチン接種記録セクション */}
        {user && id && <VaccinationSection dogId={id} userId={user.uid} />}

        {/* 血統図セクション */}
        {pedigree && (
          <Box bg="white" p={6} borderRadius="lg" shadow="md">
            <VStack align="stretch" gap={4}>
              <Heading size="md">血統図（3世代）</Heading>
              {pedigreeLoading ? (
                <Text color="gray.500" textAlign="center" py={4}>
                  読み込み中...
                </Text>
              ) : (
                <PedigreeTree pedigree={pedigree} />
              )}
            </VStack>
          </Box>
        )}

        {/* 血統書・登録証明書・遺伝子検査結果報告書セクション */}
        {user && id && (
          <Box bg="white" p={6} borderRadius="lg" shadow="md">
            <VStack align="stretch" gap={6}>
              <Heading size="md">血統書・登録証明書・遺伝子検査結果報告書</Heading>

              {/* ドキュメント表示 */}
              {documentLoading ? (
                <Text color="gray.500" textAlign="center" py={4}>
                  読み込み中...
                </Text>
              ) : (
                <DocumentViewer
                  dogId={id}
                  userId={user.uid}
                  document={pedigreeDocument}
                  onDocumentChange={handleDocumentChange}
                />
              )}

              {/* ドキュメントアップロード */}
              {!documentLoading && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                    {pedigreeDocument
                      ? 'ドキュメントを置き換え'
                      : '血統書・登録証明書・遺伝子検査結果報告書をアップロード'}
                  </Text>
                  <DocumentUploader
                    dogId={id}
                    userId={user.uid}
                    existingDocument={pedigreeDocument}
                    onUploadComplete={handleDocumentChange}
                  />
                </Box>
              )}
            </VStack>
          </Box>
        )}

        {/* 写真ギャラリー */}
        {user && id && (
          <Box bg="white" p={6} borderRadius="lg" shadow="md">
            <VStack align="stretch" gap={6}>
              <Heading size="md">写真</Heading>

              {/* 写真アップロード */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  写真をアップロード
                </Text>
                <PhotoUploader
                  dogId={id}
                  userId={user.uid}
                  onUploadComplete={() => fetchDog(id, user.uid)}
                />
              </Box>

              {/* 写真一覧 */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  登録済み写真
                </Text>
                <PhotoGallery dogId={id} userId={user.uid} onPhotosChange={() => fetchDog(id, user.uid)} />
              </Box>
            </VStack>
          </Box>
        )}

        {/* 開発中メッセージ */}
        <Box p={6} bg="blue.50" borderRadius="lg" border="1px" borderColor="blue.200">
          <VStack align="start" gap={2}>
            <Heading size="md" color="blue.800">
              🚧 機能開発中
            </Heading>
            <Text color="blue.700">
              統計ダッシュボードなどの機能は現在開発中です。
            </Text>
          </VStack>
        </Box>
      </VStack>
      </Box>

      {/* AI交配提案モーダル */}
      {user && selectedDog && selectedDog.gender === 'female' && (
        <AIMatingProposalModal
          open={isAIModalOpen}
          onClose={() => setIsAIModalOpen(false)}
          femaleId={selectedDog.id}
          femaleName={selectedDog.name}
          userId={user.uid}
        />
      )}

      {/* 削除確認ダイアログ */}
      {selectedDog && (
        <DeleteDogDialog
          dog={selectedDog}
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onDelete={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </AppLayout>
  );
};

export default DogDetailPage;
